import { Text, View } from '@react-pdf/renderer'
import type { MapFiltersType } from '~/atoms'
import { tw } from '~/components/pdfs'
import {
  getFilteredLeaseSpaces,
  sortLeaseSpaces,
  type Listing,
  type ListingLeaseSpace,
} from '~/models'
import { RATE_TYPES, cn, fmtNumberAbbr } from '~/utils'

function getLeaseSpaceRows({
  leaseSpaces,
  listing,
  showAll = false,
  isNumAbbr = false,
}: {
  leaseSpaces?: ListingLeaseSpace[]
  listing?: Listing
  showAll?: boolean
  isNumAbbr?: boolean
}) {
  const spaces = showAll
    ? [...(leaseSpaces || [])]
    : [...(leaseSpaces || [])].filter((s) => s.available)
  spaces.sort((a, b) => a.modified.localeCompare(b.modified))

  // if (spaces.length === 0 || (!showAll && spaces.length === 1)) {
  if (spaces.length === 0) {
    return { headers: [], rows: {} }
  }

  const maybeFmtNumberAbbr = (value?: number) => {
    if (isNumAbbr) {
      return fmtNumberAbbr(value)
    }
    return value?.toLocaleString()
  }

  const getSpaceRate = (space: ListingLeaseSpace) => {
    const min = space.minRate
    const max = space.maxRate
    const rateType =
      space.rateType && typeof RATE_TYPES[space.rateType] !== 'undefined'
        ? ` /${RATE_TYPES[space.rateType]}`
        : ''
    if ((!min && !max) || space.rateNegotiable) {
      return 'Negotiable'
    } else if (min && !max) {
      return `$${maybeFmtNumberAbbr(min)} ${rateType}`
    } else if (min && max && min !== max) {
      return `$${maybeFmtNumberAbbr(min)} - $${maybeFmtNumberAbbr(
        max
      )}${rateType}`
    }
    return `$${maybeFmtNumberAbbr(min)}${rateType}`
  }

  const getSquareFootage = (space: ListingLeaseSpace) => {
    const min = space.minAvailableSf
    const max = space.maxAvailableSf
    if (!min && !max) {
      return null
    } else if (min !== max) {
      return `${maybeFmtNumberAbbr(min)} - ${maybeFmtNumberAbbr(max)} SF`
    }
    return `${maybeFmtNumberAbbr(min)} SF`
  }

  const rows: { [key: string]: any } = {}
  const headers: { key: string; label: string }[] = []

  spaces.forEach((space) => {
    rows[space._id] = {
      name: space.name ? space.name : listing?.name || '-',
      floor: space.floor,
      rate: getSpaceRate(space),
      footage: getSquareFootage(space),
      acres: space.acres
        ? space.acres
            .toLocaleString(undefined, { minimumFractionDigits: 4 })
            .replace(/(\.\d*?[1-9])0+$/, '$1')
            .replace('.0000', '') + ' ac'
        : '',
      sublease: space.sublease ? 'Yes' : '',
      vacant: space.vacant ? 'Yes' : '',
      availableDate: space.availableDate?.toString()?.split('T')[0] ?? '',
      available: showAll ? (space.available ? 'Yes' : 'No') : '',
    }
  })

  const spaceHeaders = [
    { key: 'name', label: 'Name' },
    { key: 'floor', label: 'Floor' },
    { key: 'rate', label: 'Rate' },
    { key: 'footage', label: 'Footage' },
    { key: 'acres', label: 'Acres' },
    { key: 'sublease', label: 'Sublease' },
    { key: 'vacant', label: 'Vacant' },
    { key: 'availableDate', label: 'Avail Date' },
    { key: 'available', label: 'Available' },
  ]
  spaceHeaders.forEach((header) => {
    if (Object.values(rows).some((row) => row[header.key])) {
      headers.push(header)
    }
  })

  return { headers, rows }
}

export default function PdfLeaseSpaces({
  listing,
  filters,
}: {
  listing: Listing
  filters: MapFiltersType | undefined
}) {
  const sortedLeaseSpaces = sortLeaseSpaces(
    listing.leaseSpaces || [],
    listing.transactions || [],
    listing.options?.leaseSpaceSorting || 'numeric'
  )
  const { headers, rows } = getLeaseSpaceRows({
    leaseSpaces: sortedLeaseSpaces,
    listing,
    isNumAbbr: false,
    showAll: listing.status === 'leased',
  })
  const { matches, nonMatches } = getFilteredLeaseSpaces(
    filters,
    sortedLeaseSpaces,
    listing.propertyTypes[0]
  )

  if (headers.length === 0) {
    return null
  }

  // TODO Remove once we show lease spaces in a different way
  if (headers.length > 6) {
    const subleaseIndex = headers.findIndex((h) => h.key === 'sublease')
    if (subleaseIndex > -1) {
      headers.splice(subleaseIndex, 1)
    }
    const vacantIndex = headers.findIndex((h) => h.key === 'vacant')
    if (vacantIndex > -1) {
      headers.splice(vacantIndex, 1)
    }
  }

  return (
    <View wrap={false} style={tw('mt-6')}>
      <Text style={tw('font-heading mb-4 text-base leading-none')}>
        {listing.status === 'leased' ? 'Leased Spaces' : 'Lease Spaces'}
      </Text>
      <View style={tw('flex flex-row')}>
        {headers.map((header, index) => (
          <View key={header.key} style={index === 0 ? tw('flex-1') : undefined}>
            <View
              style={tw(
                cn(
                  'border-b border-slate-200 pb-1',
                  index < headers.length - 1 && 'pr-4'
                )
              )}>
              <Text style={tw('font-sans text-sm font-medium')}>
                {header.label}
              </Text>
            </View>
            {matches.map(({ _id }) => {
              if (typeof rows[_id] === 'undefined') {
                // not available
                return null
              }
              return (
                <View key={_id} style={tw('py-1 pr-4')}>
                  <Text
                    style={tw(cn('h-[12px] font-sans text-sm leading-none'))}>
                    {rows[_id][header.key]}
                  </Text>
                </View>
              )
            })}
            {nonMatches.map(({ _id }) => {
              if (typeof rows[_id] === 'undefined') {
                // not available
                return null
              }
              return (
                <View key={_id} style={tw('py-1 pr-4')}>
                  <Text
                    style={tw(
                      cn(
                        'h-[12px] font-sans text-sm leading-none text-muted-foreground opacity-50'
                      )
                    )}>
                    {rows[_id][header.key]}
                  </Text>
                </View>
              )
            })}
          </View>
        ))}
      </View>
    </View>
  )
}
